import React, { useState, useCallback, useEffect } from 'react';
import { billingAdminService } from '../services/api';
import {
  IndianRupee, TrendingUp, AlertTriangle, CreditCard,
  ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown,
  Search, RefreshCw, Gift, Settings, Clock, Shield,
  Zap, CheckCircle, XCircle, Bell, FileText, Eye, MoreVertical,
  Wallet, Receipt, Activity, Users, Calendar, Hash,
  X, Info, Copy, BookOpen, Scale, Download
} from 'lucide-react';
import './BillingManagement.css';

// ── Helpers ───────────────────────────────────────────────────
const fmt = (v) => Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const fmtINR = (v) => `₹${fmt(v)}`;

const relativeDate = (dateStr) => {
  if (!dateStr) return 'Never';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
};

const fullDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const copyId = (text) => { navigator.clipboard.writeText(text).catch(() => {}); };

const StatusBadge = ({ status }) => {
  const cfg = {
    active: 'status-active', suspended: 'status-pending', closed: 'status-disabled',
    success: 'status-active', failed: 'status-disabled', pending: 'status-pending',
    notified: 'status-info', initiated: 'status-info', cancelled: 'status-muted',
    removed: 'status-muted', expired: 'status-disabled',
  };
  return <span className={`status-badge ${cfg[status] || ''}`}>{status}</span>;
};

const ModeBadge = ({ mode }) => {
  const icons = { threshold: <Zap size={11} />, prepaid: <Wallet size={11} />, monthly: <Calendar size={11} /> };
  return <span className={`billing-mode-badge billing-mode--${mode}`}>{icons[mode]} {mode}</span>;
};

const ThresholdPill = ({ level, amount }) => (
  <span className="threshold-pill" title={`Level ${level}: ${fmtINR(amount)}`}>
    <span className="threshold-level">L{level}</span> {fmtINR(amount)}
  </span>
);

// ── Dashboard ─────────────────────────────────────────────────
const BillingDashboard = ({ onNavigateAccounts }) => {
  const [stats, setStats] = useState(null);
  const [dailyRevenue, setDailyRevenue] = useState([]);
  const [activityFeed, setActivityFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true); setError(null);
        const [data, feedData] = await Promise.all([
          billingAdminService.getStats({ days }),
          billingAdminService.getActivityFeed({ limit: 15 }),
        ]);
        setStats(data.stats);
        setDailyRevenue(data.daily_revenue || []);
        setActivityFeed(feedData.events || []);
      } catch { setError('Failed to connect to billing service'); }
      finally { setLoading(false); }
    })();
  }, [days]);

  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (error) return <div className="alert alert-error">{error}</div>;
  if (!stats) return null;

  const maxRev = Math.max(...dailyRevenue.map(x => parseFloat(x.revenue) || 0), 1);

  return (
    <>
      <div className="dashboard-grid">
        <div className="stat-card" onClick={onNavigateAccounts} style={{ cursor: 'pointer' }}>
          <h3><Users size={14} /> Total Accounts</h3>
          <div className="value">{stats.total_accounts}</div>
          <p className="stat-sub">{stats.active_accounts} active</p>
        </div>
        <div className="stat-card online">
          <h3><IndianRupee size={14} /> Revenue ({days}d)</h3>
          <div className="value">{fmtINR(stats.total_revenue)}</div>
          <p className="stat-sub">{dailyRevenue.length > 0 ? `${dailyRevenue.length} days with txns` : 'No transactions yet'}</p>
        </div>
        <div className="stat-card warning">
          <h3><AlertTriangle size={14} /> Failed Charges (7d)</h3>
          <div className="value">{stats.failed_charges_7d}</div>
          <p className="stat-sub">{stats.failed_charges_7d > 0 ? 'Requires attention' : 'All clear'}</p>
        </div>
        <div className="stat-card">
          <h3><TrendingUp size={14} /> Collection Rate</h3>
          <div className="value">
            {stats.total_revenue > 0 ? '100%' : '—'}
          </div>
          <p className="stat-sub">Successful / total charges</p>
        </div>
      </div>

      <div className="billing-card">
        <div className="billing-card-header">
          <h2><Activity size={16} /> Daily Revenue</h2>
          <div className="billing-card-actions">
            {[7, 30, 90].map(d => (
              <button key={d} className={`btn btn-sm ${days === d ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setDays(d)}>
                {d}d
              </button>
            ))}
          </div>
        </div>
        <div className="billing-card-body">
          {dailyRevenue.length === 0 ? (
            <div className="empty-state-box">
              <Receipt size={32} className="empty-icon" />
              <p>No revenue recorded in the last {days} days</p>
              <span>Revenue will appear here once charges are successfully collected</span>
            </div>
          ) : (
            <div className="revenue-chart">
              {dailyRevenue.map(d => {
                const pct = ((parseFloat(d.revenue) || 0) / maxRev) * 100;
                return (
                  <div key={d.date} className="revenue-row" title={`${d.date}: ${fmtINR(d.revenue)} (${d.charges} charges)`}>
                    <span className="revenue-date">{new Date(d.date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}</span>
                    <div className="revenue-track">
                      <div className="revenue-fill" style={{ width: `${Math.max(pct, 3)}%` }}>
                        <span className="revenue-fill-label">{d.charges} txn{Number(d.charges) !== 1 ? 's' : ''}</span>
                      </div>
                    </div>
                    <span className="revenue-amount">{fmtINR(d.revenue)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Activity Feed */}
      <div className="billing-card">
        <div className="billing-card-header">
          <h2><Bell size={16} /> Recent Activity</h2>
          <span className="text-muted" style={{ fontSize: '0.8rem' }}>Last {activityFeed.length} events across all tenants</span>
        </div>
        <div className="billing-card-body">
          {activityFeed.length === 0 ? (
            <div className="empty-state-box">
              <Activity size={32} className="empty-icon" />
              <p>No recent billing activity</p>
              <span>Events will appear here as charges, credits, and config changes occur</span>
            </div>
          ) : (
            <div className="activity-feed-list">
              {activityFeed.map(event => (
                <div key={event.id} className="activity-feed-item">
                  <span className={`event-dot dot-${getTimelineColor(event.action_type)}`} />
                  <div className="activity-feed-content">
                    <div className="activity-feed-header">
                      <span className="activity-feed-action">{getTimelineIcon(event.action_type)} {formatActionLabel(event.action_type)}</span>
                      <span className="activity-feed-time" title={fullDate(event.created_at)}>{relativeDate(event.created_at)}</span>
                    </div>
                    <div className="activity-feed-desc">{event.description}</div>
                    <div className="activity-feed-meta">
                      {event.tenant_name && <span><Users size={10} /> {event.tenant_name}</span>}
                      {event.actor_email && <span style={{ marginLeft: '0.75rem' }}>{event.actor_email}</span>}
                      {!event.actor_email && event.actor_type === 'system' && <span style={{ marginLeft: '0.75rem' }}><Zap size={10} /> system</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// ── Accounts List ─────────────────────────────────────────────
const BillingAccounts = ({ onSelectAccount }) => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25, totalCount: 0, totalPages: 0 });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [modeFilter, setModeFilter] = useState('');
  const [sorting, setSorting] = useState({ sortBy: 'created_at', sortOrder: 'desc' });

  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await billingAdminService.getAccounts({
        page: pagination.page, pageSize: pagination.pageSize,
        sortBy: sorting.sortBy, sortOrder: sorting.sortOrder,
        ...(search && { search }), ...(statusFilter && { status: statusFilter }), ...(modeFilter && { billing_mode: modeFilter }),
      });
      setAccounts(data.accounts || []);
      if (data.pagination) setPagination(prev => ({ ...prev, ...data.pagination }));
    } catch (err) { console.error('Failed to load accounts:', err); }
    finally { setLoading(false); }
  }, [pagination.page, pagination.pageSize, search, statusFilter, modeFilter, sorting]);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const handleSort = (field) => {
    setSorting(prev => ({ sortBy: field, sortOrder: prev.sortBy === field && prev.sortOrder === 'asc' ? 'desc' : 'asc' }));
  };
  const getSortIcon = (field) => {
    if (sorting.sortBy !== field) return <ArrowUpDown size={12} className="sort-icon" />;
    return sorting.sortOrder === 'asc' ? <ArrowUp size={12} className="sort-icon active" /> : <ArrowDown size={12} className="sort-icon active" />;
  };

  const showStart = Math.min(((pagination.page - 1) * pagination.pageSize) + 1, pagination.totalCount);
  const showEnd = Math.min(pagination.page * pagination.pageSize, pagination.totalCount);

  return (
    <>
      <div className="filters-bar">
        <div className="search-box">
          <input type="text" placeholder="Search by tenant name or account ID…" value={search}
            onChange={e => { setSearch(e.target.value); setPagination(p => ({ ...p, page: 1 })); }} />
        </div>
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}>
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="closed">Closed</option>
        </select>
        <select value={modeFilter} onChange={e => { setModeFilter(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}>
          <option value="">All Modes</option>
          <option value="threshold">Threshold</option>
          <option value="prepaid">Prepaid</option>
          <option value="monthly">Monthly</option>
        </select>
        <button className="btn btn-secondary btn-sm" onClick={fetchAccounts} title="Refresh"><RefreshCw size={14} /></button>
      </div>

      {loading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : accounts.length === 0 ? (
        <div className="billing-card"><div className="empty-state-box">
          <CreditCard size={32} className="empty-icon" />
          <p>No billing accounts found</p>
          <span>Accounts are auto-created when tenants first access billing APIs</span>
        </div></div>
      ) : (
        <>
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th className="sortable" onClick={() => handleSort('created_at')}>Tenant {getSortIcon('created_at')}</th>
                  <th>Mode</th>
                  <th className="sortable" onClick={() => handleSort('billing_threshold_amount')}>Threshold {getSortIcon('billing_threshold_amount')}</th>
                  <th className="sortable" onClick={() => handleSort('accrued_spend')}>Accrued Spend {getSortIcon('accrued_spend')}</th>
                  <th>Balances</th>
                  <th>Payment</th>
                  <th>Status</th>
                  <th className="sortable" onClick={() => handleSort('last_charge_at')}>Last Charge {getSortIcon('last_charge_at')}</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map(a => {
                  const noPayment = parseInt(a.active_payment_methods) === 0;
                  const hasFail = a.payment_failure_count > 0;
                  return (
                    <tr key={a.id} className={a.spending_limit_reached ? 'row-alert' : ''}>
                      <td>
                        <div className="user-cell">
                          <div className="user-avatar">{(a.tenant_name || '?').charAt(0)}</div>
                          <div className="user-info">
                            <span className="user-name clickable" onClick={() => onSelectAccount(a.id)}>{a.tenant_name || 'Unnamed'}</span>
                            <span className="user-email">
                              {a.id.slice(0, 8)}…
                              <button className="copy-btn" onClick={e => { e.stopPropagation(); copyId(a.id); }} title="Copy ID"><Copy size={10} /></button>
                            </span>
                          </div>
                        </div>
                      </td>
                      <td><ModeBadge mode={a.billing_mode} /></td>
                      <td><ThresholdPill level={a.billing_threshold_level} amount={a.billing_threshold_amount} /></td>
                      <td>
                        <span className={parseFloat(a.accrued_spend) > parseFloat(a.billing_threshold_amount) ? 'amount-over' : ''}>
                          {fmtINR(a.accrued_spend)}
                        </span>
                      </td>
                      <td>
                        <div className="balance-stack">
                          {parseFloat(a.prepaid_balance) > 0 && <span className="bal-tag prepaid"><Wallet size={10} /> {fmtINR(a.prepaid_balance)}</span>}
                          {parseFloat(a.credit_balance) > 0 && <span className="bal-tag credit"><Gift size={10} /> {fmtINR(a.credit_balance)}</span>}
                          {parseFloat(a.prepaid_balance) === 0 && parseFloat(a.credit_balance) === 0 && <span className="text-muted">—</span>}
                        </div>
                      </td>
                      <td>
                        <div className="payment-stack">
                          <span className={`pm-count ${noPayment ? 'pm-count--none' : ''}`}><CreditCard size={12} /> {a.active_payment_methods || 0}</span>
                          {hasFail && <span className="pm-fail"><XCircle size={11} /> {a.payment_failure_count}</span>}
                        </div>
                      </td>
                      <td><StatusBadge status={a.status} /></td>
                      <td className="text-muted">{relativeDate(a.last_charge_at)}</td>
                      <td>
                        <div className="actions-dropdown">
                          <button className="btn btn-secondary btn-sm"><MoreVertical size={14} /></button>
                          <div className="dropdown-menu">
                            <button className="dropdown-item" onClick={() => onSelectAccount(a.id)}><Eye size={14} /> View Details</button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 0 && (
            <div className="pagination">
              <span>Showing {showStart}–{showEnd} of {pagination.totalCount} accounts</span>
              <div className="pagination-buttons">
                <button className="btn btn-secondary btn-sm" disabled={pagination.page <= 1} onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}>
                  <ChevronLeft size={14} /> Prev
                </button>
                <button className="btn btn-secondary btn-sm" disabled={pagination.page >= pagination.totalPages} onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}>
                  Next <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

// ── Account Detail ────────────────────────────────────────────
const AccountDetail = ({ accountId, onBack }) => {
  const [account, setAccount] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [tlPage, setTlPage] = useState(1);
  const [tlPag, setTlPag] = useState({ totalCount: 0, totalPages: 0 });

  // Ledger state
  const [ledger, setLedger] = useState([]);
  const [ledgerPag, setLedgerPag] = useState({ page: 1, pageSize: 50, totalCount: 0, totalPages: 0 });
  const [ledgerSummary, setLedgerSummary] = useState(null);
  const [ledgerFilter, setLedgerFilter] = useState('');
  const [ledgerLoading, setLedgerLoading] = useState(false);

  // Invoices state
  const [invoices, setInvoices] = useState([]);
  const [invoicesPag, setInvoicesPag] = useState({ page: 1, pageSize: 25, totalCount: 0, totalPages: 0 });
  const [invoicesLoading, setInvoicesLoading] = useState(false);

  const [showCreditModal, setShowCreditModal] = useState(false);
  const [creditForm, setCreditForm] = useState({ amount: '', reason: 'promo', description: '', expires_in_days: '365' });
  const [creditLoading, setCreditLoading] = useState(false);

  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configForm, setConfigForm] = useState({});
  const [configLoading, setConfigLoading] = useState(false);

  const [message, setMessage] = useState(null);

  const fetchDetail = useCallback(async () => {
    try {
      setLoading(true);
      const [ad, td] = await Promise.all([
        billingAdminService.getAccount(accountId),
        billingAdminService.getTimeline(accountId, { page: tlPage, pageSize: 50 }),
      ]);
      setAccount(ad.account);
      setPaymentMethods(ad.paymentMethods || []);
      setTransactions(ad.recentTransactions || []);
      setTimeline(td.timeline || []);
      if (td.pagination) setTlPag(td.pagination);
      if (ad.account) {
        setConfigForm({
          pre_debit_notification_hours: ad.account.pre_debit_notification_hours || 24,
          custom_threshold_level_1: ad.account.custom_threshold_level_1 || '',
          custom_threshold_level_2: ad.account.custom_threshold_level_2 || '',
          custom_threshold_level_3: ad.account.custom_threshold_level_3 || '',
          custom_threshold_level_4: ad.account.custom_threshold_level_4 || '',
          custom_threshold_level_5: ad.account.custom_threshold_level_5 || '',
          notes: ad.account.notes || '',
        });
      }
    } catch (err) { console.error('Detail fetch failed:', err); }
    finally { setLoading(false); }
  }, [accountId, tlPage]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  // Ledger fetch
  const fetchLedger = useCallback(async () => {
    try {
      setLedgerLoading(true);
      const data = await billingAdminService.getAccountLedger(accountId, {
        page: ledgerPag.page, pageSize: ledgerPag.pageSize,
        ...(ledgerFilter && { entry_type: ledgerFilter }),
      });
      setLedger(data.ledger || []);
      setLedgerSummary(data.summary || null);
      if (data.pagination) setLedgerPag(prev => ({ ...prev, ...data.pagination }));
    } catch (err) { console.error('Ledger fetch failed:', err); }
    finally { setLedgerLoading(false); }
  }, [accountId, ledgerPag.page, ledgerPag.pageSize, ledgerFilter]);

  useEffect(() => { if (tab === 'ledger') fetchLedger(); }, [tab, fetchLedger]);

  // Invoices fetch
  const fetchInvoices = useCallback(async () => {
    try {
      setInvoicesLoading(true);
      const data = await billingAdminService.getInvoices({
        billing_account_id: accountId,
        page: invoicesPag.page, pageSize: invoicesPag.pageSize,
      });
      setInvoices(data.invoices || []);
      if (data.pagination) setInvoicesPag(prev => ({ ...prev, ...data.pagination }));
    } catch (err) { console.error('Invoices fetch failed:', err); }
    finally { setInvoicesLoading(false); }
  }, [accountId, invoicesPag.page, invoicesPag.pageSize]);

  useEffect(() => { if (tab === 'invoices') fetchInvoices(); }, [tab, fetchInvoices]);

  const handleGrantCredit = async (e) => {
    e.preventDefault(); setCreditLoading(true); setMessage(null);
    try {
      await billingAdminService.grantCredit({
        billing_account_id: accountId, amount: parseFloat(creditForm.amount),
        reason: creditForm.reason, description: creditForm.description,
        expires_in_days: parseInt(creditForm.expires_in_days),
      });
      setMessage({ type: 'success', text: `${fmtINR(creditForm.amount)} credit granted` });
      setShowCreditModal(false);
      setCreditForm({ amount: '', reason: 'promo', description: '', expires_in_days: '365' });
      fetchDetail();
    } catch (err) { setMessage({ type: 'error', text: err.response?.data?.error || err.response?.data?.details || 'Grant failed' }); }
    finally { setCreditLoading(false); }
  };

  const handleUpdateConfig = async (e) => {
    e.preventDefault(); setConfigLoading(true); setMessage(null);
    try {
      await billingAdminService.updateConfig(accountId, configForm);
      setMessage({ type: 'success', text: 'Configuration updated' });
      setShowConfigModal(false); fetchDetail();
    } catch (err) { setMessage({ type: 'error', text: err.response?.data?.error || err.response?.data?.details || 'Update failed' }); }
    finally { setConfigLoading(false); }
  };

  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (!account) return <div className="alert alert-error">Account not found</div>;

  const spendPct = parseFloat(account.billing_threshold_amount) > 0
    ? Math.min((parseFloat(account.accrued_spend) / parseFloat(account.billing_threshold_amount)) * 100, 100) : 0;

  return (
    <div style={{ maxWidth: 1100 }}>
      {/* Header */}
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button className="btn btn-secondary btn-sm" onClick={onBack}><ChevronLeft size={14} /> Back</button>
          <div>
            <h1 style={{ fontSize: '1.5rem' }}>{account.tenant_name || 'Billing Account'}</h1>
            <div className="detail-meta">
              <StatusBadge status={account.status} /> <ModeBadge mode={account.billing_mode} />
              <span className="meta-id"><Hash size={11} /> {account.id.slice(0, 8)} <button className="copy-btn" onClick={() => copyId(account.id)} title="Copy ID"><Copy size={10} /></button></span>
              <span className="meta-date"><Calendar size={11} /> Created {relativeDate(account.created_at)}</span>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn btn-secondary" onClick={() => setShowConfigModal(true)}><Settings size={14} /> Config</button>
          <button className="btn btn-primary" onClick={() => setShowCreditModal(true)}><Gift size={14} /> Grant Credit</button>
        </div>
      </div>

      {message && (
        <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-error'}`}>
          {message.text}
          <button style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => setMessage(null)}><X size={14} /></button>
        </div>
      )}

      {account.spending_limit_reached && (
        <div className="alert alert-error"><AlertTriangle size={14} /> Spending limit reached — campaigns may be paused. Spend: {fmtINR(account.accrued_spend)}, Limit: {fmtINR(account.account_spending_limit)}</div>
      )}
      {parseInt(account.payment_failure_count) > 0 && (
        <div className="alert alert-error" style={{ background: '#fff3cd', color: '#856404', borderColor: '#ffc107' }}><AlertTriangle size={14} /> {account.payment_failure_count} payment failure(s)</div>
      )}

      {/* Financial KPIs */}
      <div className="dashboard-grid">
        <div className="stat-card">
          <h3><IndianRupee size={14} /> Accrued Spend</h3>
          <div className="value">{fmtINR(account.accrued_spend)}</div>
          <div className="spend-progress"><div className="spend-progress-fill" style={{ width: `${spendPct}%`, background: spendPct > 90 ? '#dc3545' : spendPct > 70 ? '#ffc107' : '#28a745' }} /></div>
          <p className="stat-sub">{Math.round(spendPct)}% of {fmtINR(account.billing_threshold_amount)} threshold</p>
        </div>
        <div className="stat-card">
          <h3><Wallet size={14} /> Prepaid Balance</h3>
          <div className="value">{fmtINR(account.prepaid_balance)}</div>
          <p className="stat-sub">Available for ad spend</p>
        </div>
        <div className="stat-card online">
          <h3><Gift size={14} /> Credit Balance</h3>
          <div className="value">{fmtINR(account.credit_balance)}</div>
          <p className="stat-sub">From promotional grants</p>
        </div>
        <div className="stat-card">
          <h3><CheckCircle size={14} /> Charges</h3>
          <div className="value">{account.successful_charge_count || 0}</div>
          <p className="stat-sub">{account.payment_failure_count > 0 ? <span style={{ color: '#dc3545' }}>{account.payment_failure_count} failed</span> : 'No failures'}</p>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="billing-section-tabs">
        {[
          { key: 'overview', label: 'Overview', icon: <Info size={14} /> },
          { key: 'payments', label: `Payment Methods (${paymentMethods.length})`, icon: <CreditCard size={14} /> },
          { key: 'transactions', label: `Transactions (${transactions.length})`, icon: <Receipt size={14} /> },
          { key: 'ledger', label: `Ledger (${ledgerPag.totalCount})`, icon: <BookOpen size={14} /> },
          { key: 'invoices', label: `Invoices (${invoicesPag.totalCount})`, icon: <FileText size={14} /> },
          { key: 'timeline', label: `Timeline (${tlPag.totalCount})`, icon: <Clock size={14} /> },
        ].map(t => (
          <button key={t.key} className={`billing-section-tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Overview */}
      {tab === 'overview' && (
        <div className="device-detail" style={{ marginTop: 0 }}>
          <h2><Shield size={16} /> Account Configuration</h2>
          <div className="detail-grid">
            <DetailItem label="Billing Mode"><ModeBadge mode={account.billing_mode} /></DetailItem>
            <DetailItem label="Threshold Level"><ThresholdPill level={account.billing_threshold_level} amount={account.billing_threshold_amount} /></DetailItem>
            <DetailItem label="Pre-Debit Notice">{account.pre_debit_notification_hours || 24} hours</DetailItem>
            <DetailItem label="Notifications">{account.enable_notifications !== false ? <><CheckCircle size={12} style={{ color: '#27ae60' }} /> Enabled</> : <><XCircle size={12} style={{ color: '#e74c3c' }} /> Disabled</>}</DetailItem>
            <DetailItem label="Spending Limit">{account.account_spending_limit ? fmtINR(account.account_spending_limit) : 'No limit'}</DetailItem>
            <DetailItem label="Limit Reached">{account.spending_limit_reached ? <><AlertTriangle size={12} style={{ color: '#e74c3c' }} /> Yes</> : 'No'}</DetailItem>
            <DetailItem label="Currency">{account.currency}</DetailItem>
            <DetailItem label="Billing Day">{account.billing_day_of_month}{ordSuffix(account.billing_day_of_month)} of month</DetailItem>
            <DetailItem label="Auto-Created">{fullDate(account.auto_created_at)}</DetailItem>
            <DetailItem label="First Payment Added">{account.first_payment_method_added_at ? fullDate(account.first_payment_method_added_at) : 'Not yet'}</DetailItem>
            <DetailItem label="Last Charge">{account.last_charge_at ? fullDate(account.last_charge_at) : 'Never'}</DetailItem>
            <DetailItem label="Last Invoice">{account.last_invoice_at ? fullDate(account.last_invoice_at) : 'Never'}</DetailItem>
          </div>

          {(account.custom_threshold_level_1 || account.custom_threshold_level_2 || account.custom_threshold_level_3) && (
            <>
              <h2 style={{ marginTop: '2rem' }}><Settings size={16} /> Custom Threshold Overrides</h2>
              <div className="detail-grid">
                {[1, 2, 3, 4, 5].map(l => {
                  const val = account[`custom_threshold_level_${l}`];
                  return val ? <DetailItem key={l} label={`Level ${l}`}>{fmtINR(val)}</DetailItem> : null;
                })}
              </div>
            </>
          )}

          {account.notes && (
            <>
              <h2 style={{ marginTop: '2rem' }}><FileText size={16} /> Admin Notes</h2>
              <p style={{ margin: 0, color: '#333', background: '#f8f9fa', padding: '1rem', borderRadius: '8px' }}>{account.notes}</p>
            </>
          )}
        </div>
      )}

      {/* Payment Methods */}
      {tab === 'payments' && (
        <div className="device-detail" style={{ marginTop: 0 }}>
          <h2><CreditCard size={16} /> Payment Methods</h2>
          {paymentMethods.length === 0 ? (
            <div className="empty-state-box"><CreditCard size={32} className="empty-icon" /><p>No payment methods registered</p><span>Tenant needs to register a UPI Autopay or eNACH mandate</span></div>
          ) : (
            <div className="data-table" style={{ boxShadow: 'none' }}>
              <table>
                <thead><tr>
                  <th>Method</th><th>Type</th><th>Level</th><th>Max Amount</th><th>Status</th><th>Primary</th><th>Registered</th>
                </tr></thead>
                <tbody>
                  {paymentMethods.map(pm => (
                    <tr key={pm.id}>
                      <td>
                        <div className="user-cell">
                          <CreditCard size={14} style={{ color: '#666' }} />
                          <div className="user-info">
                            <span className="user-name">{pm.display_label || '—'}</span>
                            <span className="user-email">{pm.provider}</span>
                          </div>
                        </div>
                      </td>
                      <td><span className="billing-mode-badge billing-mode--threshold">{pm.mandate_type || pm.method_type}</span></td>
                      <td>L{pm.threshold_level || '—'}</td>
                      <td>{pm.max_amount ? fmtINR(pm.max_amount) : '—'}</td>
                      <td><StatusBadge status={pm.status} /></td>
                      <td>{pm.is_primary ? <CheckCircle size={14} style={{ color: '#27ae60' }} /> : <span className="text-muted">—</span>}</td>
                      <td>{relativeDate(pm.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Transactions */}
      {tab === 'transactions' && (
        <div className="device-detail" style={{ marginTop: 0 }}>
          <h2><Receipt size={16} /> Recent Transactions</h2>
          {transactions.length === 0 ? (
            <div className="empty-state-box"><Receipt size={32} className="empty-icon" /><p>No transactions yet</p><span>Transactions appear when charges are initiated against this account</span></div>
          ) : (
            <div className="data-table" style={{ boxShadow: 'none' }}>
              <table>
                <thead><tr>
                  <th>Amount</th><th>Status</th><th>Merchant Ref</th><th>Provider</th><th>Initiated</th><th>Notified</th><th>Completed</th><th>Failure</th>
                </tr></thead>
                <tbody>
                  {transactions.map(txn => (
                    <tr key={txn.id} className={txn.status === 'failed' ? 'row-failed' : ''}>
                      <td><strong>{fmtINR(txn.amount)}</strong></td>
                      <td><StatusBadge status={txn.status} /></td>
                      <td><code className="mono-sm" title={txn.merchant_request_num}>{txn.merchant_request_num ? txn.merchant_request_num.slice(0, 18) : '—'}</code></td>
                      <td>{txn.provider}</td>
                      <td>{relativeDate(txn.initiated_at)}</td>
                      <td>{txn.notified_at ? relativeDate(txn.notified_at) : <span className="text-muted">—</span>}</td>
                      <td>{txn.completed_at ? relativeDate(txn.completed_at) : <span className="text-muted">—</span>}</td>
                      <td>{txn.failure_reason ? <span className="failure-text" title={txn.failure_reason}><XCircle size={11} /> {txn.failure_reason.length > 35 ? txn.failure_reason.slice(0, 35) + '…' : txn.failure_reason}</span> : <span className="text-muted">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Ledger */}
      {tab === 'ledger' && (
        <div className="device-detail" style={{ marginTop: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2><BookOpen size={16} /> Account Ledger</h2>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <select value={ledgerFilter} onChange={e => { setLedgerFilter(e.target.value); setLedgerPag(p => ({ ...p, page: 1 })); }}
                style={{ padding: '0.4rem 0.6rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.8rem' }}>
                <option value="">All Types</option>
                <option value="charge_debit">Charge Debit</option>
                <option value="charge_refund">Charge Refund</option>
                <option value="credit_applied">Credit Applied</option>
                <option value="credit_granted">Credit Granted</option>
                <option value="credit_revoked">Credit Revoked</option>
                <option value="prepaid_topup">Prepaid Top-up</option>
                <option value="manual_adjustment">Manual Adjustment</option>
              </select>
              <button className="btn btn-secondary btn-sm" onClick={fetchLedger} title="Refresh"><RefreshCw size={14} /></button>
            </div>
          </div>

          {ledgerSummary && (
            <div className="dashboard-grid" style={{ marginBottom: '1rem' }}>
              <div className="stat-card">
                <h3><TrendingUp size={14} /> Total Debits</h3>
                <div className="value" style={{ color: '#dc3545' }}>{fmtINR(ledgerSummary.total_debits)}</div>
              </div>
              <div className="stat-card">
                <h3><Gift size={14} /> Total Credits</h3>
                <div className="value" style={{ color: '#27ae60' }}>{fmtINR(ledgerSummary.total_credits)}</div>
              </div>
              <div className="stat-card">
                <h3><Scale size={14} /> Net</h3>
                <div className="value">{fmtINR(ledgerSummary.total_debits - ledgerSummary.total_credits)}</div>
                <p className="stat-sub">{ledgerSummary.unique_types} entry type{ledgerSummary.unique_types !== 1 ? 's' : ''}</p>
              </div>
            </div>
          )}

          {ledgerLoading ? (
            <div className="loading"><div className="spinner" /></div>
          ) : ledger.length === 0 ? (
            <div className="empty-state-box"><BookOpen size={32} className="empty-icon" /><p>No ledger entries</p><span>Entries appear as charges, credits, and adjustments flow through the account</span></div>
          ) : (
            <>
              <div className="data-table" style={{ boxShadow: 'none' }}>
                <table>
                  <thead><tr>
                    <th>Type</th><th>Amount</th><th>Running Balance</th><th>Description</th><th>Reference</th><th>Date</th>
                  </tr></thead>
                  <tbody>
                    {ledger.map(entry => (
                      <tr key={entry.id}>
                        <td><span className={`billing-mode-badge billing-mode--${parseFloat(entry.amount) >= 0 ? 'threshold' : 'prepaid'}`}>{(entry.entry_type || '').replace(/_/g, ' ')}</span></td>
                        <td><strong style={{ color: parseFloat(entry.amount) >= 0 ? '#dc3545' : '#27ae60' }}>{parseFloat(entry.amount) >= 0 ? '+' : ''}{fmtINR(entry.amount)}</strong></td>
                        <td>{fmtINR(entry.running_balance)}</td>
                        <td style={{ maxWidth: 250 }}><span title={entry.description}>{entry.description ? (entry.description.length > 50 ? entry.description.slice(0, 50) + '…' : entry.description) : '—'}</span></td>
                        <td>{entry.reference_type ? <><span className="text-muted">{entry.reference_type}:</span> <code className="mono-sm">{(entry.reference_id || '').slice(0, 12)}</code></> : <span className="text-muted">—</span>}</td>
                        <td>{relativeDate(entry.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {ledgerPag.totalPages > 1 && (
                <div className="pagination" style={{ marginTop: '1rem' }}>
                  <span>Page {ledgerPag.page} of {ledgerPag.totalPages} ({ledgerPag.totalCount} entries)</span>
                  <div className="pagination-buttons">
                    <button className="btn btn-secondary btn-sm" disabled={ledgerPag.page <= 1} onClick={() => setLedgerPag(p => ({ ...p, page: p.page - 1 }))}><ChevronLeft size={14} /> Prev</button>
                    <button className="btn btn-secondary btn-sm" disabled={ledgerPag.page >= ledgerPag.totalPages} onClick={() => setLedgerPag(p => ({ ...p, page: p.page + 1 }))}> Next <ChevronRight size={14} /></button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Invoices */}
      {tab === 'invoices' && (
        <div className="device-detail" style={{ marginTop: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2><FileText size={16} /> Invoices</h2>
            <button className="btn btn-secondary btn-sm" onClick={fetchInvoices} title="Refresh"><RefreshCw size={14} /></button>
          </div>
          {invoicesLoading ? (
            <div className="loading"><div className="spinner" /></div>
          ) : invoices.length === 0 ? (
            <div className="empty-state-box"><FileText size={32} className="empty-icon" /><p>No invoices generated</p><span>Invoices are auto-generated when charges are successfully collected</span></div>
          ) : (
            <>
              <div className="data-table" style={{ boxShadow: 'none' }}>
                <table>
                  <thead><tr>
                    <th>Invoice #</th><th>Period</th><th>Total</th><th>Status</th><th>Items</th><th>Generated</th>
                  </tr></thead>
                  <tbody>
                    {invoices.map(inv => (
                      <tr key={inv.id}>
                        <td><code className="mono-sm" title={inv.invoice_number}>{inv.invoice_number || inv.id.slice(0, 12)}</code></td>
                        <td>{inv.period_start ? <>{new Date(inv.period_start).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })} — {new Date(inv.period_end).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}</> : '—'}</td>
                        <td><strong>{fmtINR(inv.total)}</strong></td>
                        <td><StatusBadge status={inv.status || 'generated'} /></td>
                        <td>{inv.line_item_count || '—'}</td>
                        <td>{relativeDate(inv.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {invoicesPag.totalPages > 1 && (
                <div className="pagination" style={{ marginTop: '1rem' }}>
                  <span>Page {invoicesPag.page} of {invoicesPag.totalPages} ({invoicesPag.totalCount} invoices)</span>
                  <div className="pagination-buttons">
                    <button className="btn btn-secondary btn-sm" disabled={invoicesPag.page <= 1} onClick={() => setInvoicesPag(p => ({ ...p, page: p.page - 1 }))}><ChevronLeft size={14} /> Prev</button>
                    <button className="btn btn-secondary btn-sm" disabled={invoicesPag.page >= invoicesPag.totalPages} onClick={() => setInvoicesPag(p => ({ ...p, page: p.page + 1 }))}> Next <ChevronRight size={14} /></button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Timeline */}
      {tab === 'timeline' && (
        <div className="device-detail" style={{ marginTop: 0 }}>
          <h2><Clock size={16} /> Billing History <span className="text-muted" style={{ fontWeight: 400, fontSize: '0.9rem' }}>({tlPag.totalCount} events)</span></h2>
          {timeline.length === 0 ? (
            <div className="empty-state-box"><Clock size={32} className="empty-icon" /><p>No billing history</p><span>Events appear as charges, credits, and config changes occur</span></div>
          ) : (
            <div className="billing-timeline">
              {timeline.map(entry => (
                <div key={entry.id} className={`tl-entry tl-entry--${getTimelineColor(entry.action_type)}`}>
                  <div className="tl-dot" />
                  <div className="tl-connector" />
                  <div className="tl-body">
                    <div className="tl-header">
                      <span className="tl-action">{getTimelineIcon(entry.action_type)} {formatActionLabel(entry.action_type)}</span>
                      <span className="tl-time" title={fullDate(entry.created_at)}>{relativeDate(entry.created_at)}</span>
                    </div>
                    <div className="tl-desc">{entry.description}</div>
                    {entry.actor_email && <div className="tl-actor"><Users size={10} /> {entry.actor_email}</div>}
                    {!entry.actor_email && entry.actor_type === 'system' && <div className="tl-actor"><Zap size={10} /> system</div>}
                    {entry.metadata && Object.keys(entry.metadata).length > 0 && (
                      <details className="tl-details">
                        <summary>Metadata</summary>
                        <pre>{JSON.stringify(entry.metadata, null, 2)}</pre>
                      </details>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {tlPag.totalPages > 1 && (
            <div className="pagination" style={{ marginTop: '1rem' }}>
              <span>Page {tlPage} of {tlPag.totalPages}</span>
              <div className="pagination-buttons">
                <button className="btn btn-secondary btn-sm" disabled={tlPage <= 1} onClick={() => setTlPage(p => p - 1)}><ChevronLeft size={14} /> Newer</button>
                <button className="btn btn-secondary btn-sm" disabled={tlPage >= tlPag.totalPages} onClick={() => setTlPage(p => p + 1)}>Older <ChevronRight size={14} /></button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Credit Grant Modal */}
      {showCreditModal && (
        <div className="modal-overlay" onClick={() => setShowCreditModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h2><Gift size={18} /> Grant Ad Credit</h2><button className="modal-close" onClick={() => setShowCreditModal(false)}><X size={18} /></button></div>
            <form onSubmit={handleGrantCredit}>
              <div className="modal-body">
                <div className="modal-info-banner">Granting credit to <strong>{account.tenant_name}</strong>. Credits follow FIFO — oldest consumed first.</div>
                <div className="form-group">
                  <label>Amount (₹) *</label>
                  <input type="number" min="1" step="0.01" required placeholder="e.g. 500" value={creditForm.amount} onChange={e => setCreditForm(f => ({ ...f, amount: e.target.value }))} />
                  <span className="form-hint">Minimum ₹1. Added to the account's credit balance.</span>
                </div>
                <div className="form-group">
                  <label>Reason *</label>
                  <select value={creditForm.reason} onChange={e => setCreditForm(f => ({ ...f, reason: e.target.value }))}>
                    <option value="promo">Promotional</option><option value="refund">Refund</option>
                    <option value="compensation">Compensation</option><option value="partner">Partner Program</option>
                    <option value="manual">Manual Adjustment</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <input type="text" value={creditForm.description} onChange={e => setCreditForm(f => ({ ...f, description: e.target.value }))} placeholder="E.g. onboarding bonus, service disruption…" />
                </div>
                <div className="form-group">
                  <label>Expires in (days)</label>
                  <input type="number" min="1" max="730" value={creditForm.expires_in_days} onChange={e => setCreditForm(f => ({ ...f, expires_in_days: e.target.value }))} />
                  <span className="form-hint">Default: 365 days. Unused credit expires after this period.</span>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreditModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={creditLoading}>
                  {creditLoading ? <><div className="btn-spinner" /> Granting…</> : <><Gift size={14} /> Grant Credit</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Config Modal */}
      {showConfigModal && (
        <div className="modal-overlay" onClick={() => setShowConfigModal(false)}>
          <div className="modal modal-wide" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h2><Settings size={18} /> Account Configuration</h2><button className="modal-close" onClick={() => setShowConfigModal(false)}><X size={18} /></button></div>
            <form onSubmit={handleUpdateConfig}>
              <div className="modal-body">
                <div className="form-section"><h3>Notification Settings</h3>
                  <div className="form-group">
                    <label>Pre-Debit Notification (hours)</label>
                    <input type="number" min="1" max="168" value={configForm.pre_debit_notification_hours} onChange={e => setConfigForm(f => ({ ...f, pre_debit_notification_hours: e.target.value }))} />
                    <span className="form-hint">Hours before charge execution (1–168). RBI requires minimum 24h.</span>
                  </div>
                </div>
                <div className="form-section"><h3>Custom Threshold Overrides</h3>
                  <span className="form-help">Override the default threshold ladder (₹500→₹2K→₹5K→₹10K→₹25K). Leave blank for defaults.</span>
                  <div className="form-row" style={{ marginTop: '0.75rem' }}>
                    {[1, 2, 3, 4, 5].map(l => (
                      <div className="form-group" key={l}>
                        <label>Level {l} (₹)</label>
                        <input type="number" min="0" step="1" placeholder="Default" value={configForm[`custom_threshold_level_${l}`]} onChange={e => setConfigForm(f => ({ ...f, [`custom_threshold_level_${l}`]: e.target.value }))} />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="form-section"><h3>Admin Notes</h3>
                  <div className="form-group">
                    <label>Notes</label>
                    <input type="text" value={configForm.notes} onChange={e => setConfigForm(f => ({ ...f, notes: e.target.value }))} placeholder="Internal notes about this account…" />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowConfigModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={configLoading}>
                  {configLoading ? <><div className="btn-spinner" /> Saving…</> : <><Settings size={14} /> Save Configuration</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Small components ──────────────────────────────────────────
const DetailItem = ({ label, children }) => (
  <div className="detail-item"><label>{label}</label><span>{children}</span></div>
);

function ordSuffix(n) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

// ── Timeline Helpers ──────────────────────────────────────────
function getTimelineColor(type) {
  if (['charge_success', 'mandate_approved', 'campaigns_resumed'].includes(type)) return 'success';
  if (['charge_failed', 'spending_limit_reached', 'campaigns_paused'].includes(type)) return 'danger';
  if (['charge_initiated', 'charge_notified'].includes(type)) return 'info';
  if (['credit_granted', 'credit_applied'].includes(type)) return 'purple';
  if (['invoice_generated'].includes(type)) return 'blue';
  return 'default';
}

function getTimelineIcon(type) {
  const m = {
    account_created: <Shield size={13} />, mandate_registered: <CreditCard size={13} />,
    mandate_approved: <CheckCircle size={13} />, mandate_upgraded: <TrendingUp size={13} />,
    charge_initiated: <Zap size={13} />, charge_notified: <Bell size={13} />,
    charge_success: <CheckCircle size={13} />, charge_failed: <XCircle size={13} />,
    spending_limit_reached: <AlertTriangle size={13} />, campaigns_paused: <AlertTriangle size={13} />,
    campaigns_resumed: <CheckCircle size={13} />, credit_granted: <Gift size={13} />,
    credit_applied: <Wallet size={13} />, credit_expired: <Clock size={13} />,
    invoice_generated: <FileText size={13} />, config_updated: <Settings size={13} />,
  };
  return m[type] || <Activity size={13} />;
}

function formatActionLabel(type) {
  const m = {
    account_created: 'Account Created', mandate_registered: 'Mandate Registered',
    mandate_approved: 'Mandate Approved', mandate_upgraded: 'Mandate Upgraded',
    charge_initiated: 'Charge Initiated', charge_notified: 'Pre-Debit Notification',
    charge_success: 'Charge Successful', charge_failed: 'Charge Failed',
    spending_limit_reached: 'Spending Limit Hit', campaigns_paused: 'Campaigns Paused',
    campaigns_resumed: 'Campaigns Resumed', credit_granted: 'Credit Granted',
    credit_applied: 'Credit Applied', credit_expired: 'Credit Expired',
    invoice_generated: 'Invoice Generated', config_updated: 'Config Updated',
  };
  return m[type] || type.replace(/_/g, ' ');
}

// ── Main Page ─────────────────────────────────────────────────
const BillingManagement = ({ initialView }) => {
  const [activeView, setActiveView] = useState(initialView || 'dashboard');
  const [selectedAccountId, setSelectedAccountId] = useState(null);

  if (selectedAccountId) {
    return <AccountDetail accountId={selectedAccountId} onBack={() => { setSelectedAccountId(null); setActiveView('accounts'); }} />;
  }

  return (
    <div className="billing-management">
      <div className="page-header"><h1>Billing Management</h1></div>
      <div className="billing-section-tabs" style={{ marginBottom: '1.5rem' }}>
        <button className={`billing-section-tab ${activeView === 'dashboard' ? 'active' : ''}`} onClick={() => setActiveView('dashboard')}><Activity size={14} /> Dashboard</button>
        <button className={`billing-section-tab ${activeView === 'accounts' ? 'active' : ''}`} onClick={() => setActiveView('accounts')}><Users size={14} /> Accounts</button>
      </div>
      {activeView === 'dashboard' && <BillingDashboard onNavigateAccounts={() => setActiveView('accounts')} />}
      {activeView === 'accounts' && <BillingAccounts onSelectAccount={id => setSelectedAccountId(id)} />}
    </div>
  );
};

export default BillingManagement;
